import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ChevronDown, X, Check, GripVertical } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

/**
 * Dropdown de Extra Labor Presets com CRUD inline pra admin.
 * - Qualquer usuário: clica no item pra adicionar ao extra_items do bill
 * - Admin: além de adicionar, tem botões de editar/deletar em cada item
 *          e um "+" pra criar novo preset (via modal)
 */
const ExtraLaborPresetsDropdown = ({ isAdmin, onSelectPreset, language = 'pt-BR' }) => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | null
  const [modalDesc, setModalDesc] = useState('');
  const [modalPrice, setModalPrice] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const wrapperRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/extra-labor-presets`, { headers: authHeaders() });
      setPresets(data.presets || []);
    } catch (e) {
      console.warn('Falha ao carregar presets:', e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const openAdd = () => {
    setModalMode('add');
    setEditingId(null);
    setModalDesc('');
    setModalPrice('');
  };

  const openEdit = (p) => {
    setModalMode('edit');
    setEditingId(p.id);
    setModalDesc(p.description);
    setModalPrice(String(p.unit_price));
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setModalDesc('');
    setModalPrice('');
  };

  const handleSave = async () => {
    const desc = modalDesc.trim();
    const price = parseFloat(modalPrice);
    if (!desc) return toast.error(language === 'pt-BR' ? 'Preencha a descrição' : 'Fill description');
    if (isNaN(price) || price < 0) return toast.error(language === 'pt-BR' ? 'Preço inválido' : 'Invalid price');
    setSaving(true);
    try {
      if (modalMode === 'add') {
        await axios.post(`${API}/extra-labor-presets`, { description: desc, unit_price: price }, { headers: authHeaders() });
        toast.success(language === 'pt-BR' ? 'Preset adicionado!' : 'Preset added!');
      } else {
        await axios.put(`${API}/extra-labor-presets/${editingId}`, { description: desc, unit_price: price }, { headers: authHeaders() });
        toast.success(language === 'pt-BR' ? 'Preset atualizado' : 'Preset updated');
      }
      closeModal();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (language === 'pt-BR' ? 'Falha ao salvar' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p, evt) => {
    evt.stopPropagation();
    if (!window.confirm(language === 'pt-BR' ? `Deletar preset "${p.description}"?` : `Delete preset "${p.description}"?`)) return;
    try {
      await axios.delete(`${API}/extra-labor-presets/${p.id}`, { headers: authHeaders() });
      toast.success(language === 'pt-BR' ? 'Preset deletado' : 'Preset deleted');
      await load();
    } catch (e) {
      toast.error(language === 'pt-BR' ? 'Falha ao deletar' : 'Failed to delete');
    }
  };

  const handlePick = (p) => {
    onSelectPreset(p);
    setOpen(false);
  };

  // ============ DRAG-TO-REORDER (admin only) ============
  const onDragStart = (idx, e) => {
    setDragIndex(idx);
    // Necessário para o drag funcionar no Chrome/Safari
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { /* noop */ }
    }
  };

  const onDragOver = (idx, e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };

  const onDragLeave = () => setDragOverIndex(null);

  const onDrop = async (idx, e) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (from === null || from === idx) return;
    // Reordena localmente (otimista)
    const reordered = [...presets];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(idx, 0, moved);
    setPresets(reordered);
    // Persiste no backend
    try {
      await axios.patch(
        `${API}/extra-labor-presets/reorder`,
        { ordered_ids: reordered.map(p => p.id) },
        { headers: authHeaders() }
      );
    } catch (err) {
      toast.error(language === 'pt-BR' ? 'Falha ao reordenar' : 'Failed to reorder');
      await load(); // reverte caso falhe
    }
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <>
      <div ref={wrapperRef} className="relative inline-block" data-testid="extra-labor-presets-dropdown">
        <button
          type="button"
          data-testid="extra-labor-presets-toggle"
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-2 px-3 py-2 border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 rounded-md text-sm font-medium min-w-[240px] justify-between"
        >
          <span>{language === 'pt-BR' ? '+ Adicionar item pré-configurado…' : '+ Add preset item…'}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            data-testid="extra-labor-presets-menu"
            className="absolute left-0 top-full mt-1 min-w-[300px] max-w-[420px] bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-[320px] overflow-y-auto"
          >
            {loading ? (
              <div className="p-3 text-sm text-gray-500 text-center">
                {language === 'pt-BR' ? 'Carregando...' : 'Loading...'}
              </div>
            ) : presets.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 italic text-center">
                {isAdmin
                  ? (language === 'pt-BR' ? 'Nenhum preset. Clique em "+ Novo preset" abaixo.' : 'No presets. Click "+ New preset" below.')
                  : (language === 'pt-BR' ? 'Nenhum preset. Peça ao admin para adicionar.' : 'No presets. Ask admin to add.')}
              </div>
            ) : (
              <ul className="py-1">
                {presets.map((p, idx) => (
                  <li
                    key={p.id}
                    data-testid={`preset-item-${p.id}`}
                    draggable={isAdmin}
                    onDragStart={isAdmin ? (e) => onDragStart(idx, e) : undefined}
                    onDragOver={isAdmin ? (e) => onDragOver(idx, e) : undefined}
                    onDragLeave={isAdmin ? onDragLeave : undefined}
                    onDrop={isAdmin ? (e) => onDrop(idx, e) : undefined}
                    onDragEnd={isAdmin ? onDragEnd : undefined}
                    className={`flex items-center gap-2 px-2 py-2 hover:bg-indigo-50 cursor-pointer group border-b last:border-b-0 transition-colors ${
                      dragIndex === idx ? 'opacity-40' : ''
                    } ${
                      dragOverIndex === idx && dragIndex !== idx ? 'bg-indigo-100 border-t-2 border-t-indigo-500' : ''
                    }`}
                    onClick={() => handlePick(p)}
                  >
                    {isAdmin && (
                      <span
                        className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0"
                        title={language === 'pt-BR' ? 'Arraste para reordenar' : 'Drag to reorder'}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`preset-drag-${p.id}`}
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{p.description}</div>
                      <div className="text-xs text-gray-500 font-mono">${p.unit_price.toFixed(2)}</div>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          data-testid={`preset-edit-${p.id}`}
                          onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                          className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                          title={language === 'pt-BR' ? 'Editar' : 'Edit'}
                        ><Edit2 className="h-3.5 w-3.5" /></button>
                        <button
                          type="button"
                          data-testid={`preset-delete-${p.id}`}
                          onClick={(e) => handleDelete(p, e)}
                          className="p-1.5 rounded hover:bg-red-100 text-red-600"
                          title={language === 'pt-BR' ? 'Deletar' : 'Delete'}
                        ><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {isAdmin && (
              <div className="border-t p-2 sticky bottom-0 bg-white">
                <button
                  type="button"
                  data-testid="preset-add-new-btn"
                  onClick={openAdd}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white text-xs font-semibold"
                >
                  <Plus className="h-4 w-4" />
                  {language === 'pt-BR' ? 'Novo preset' : 'New preset'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de add/edit */}
      {modalMode && (
        <div
          data-testid="preset-modal-backdrop"
          className="fixed inset-0 bg-black/50 z-[100000] flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {modalMode === 'add'
                  ? (language === 'pt-BR' ? '➕ Novo Extra Labor Preset' : '➕ New Extra Labor Preset')
                  : (language === 'pt-BR' ? '✏️ Editar Preset' : '✏️ Edit Preset')}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                data-testid="preset-modal-close"
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              ><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">
                  {language === 'pt-BR' ? 'Descrição' : 'Description'}
                </label>
                <Input
                  data-testid="preset-modal-desc"
                  type="text"
                  autoFocus
                  placeholder={language === 'pt-BR' ? 'Ex: Hotel, Materiais, Alimentação…' : 'Ex: Hotel, Materials, Food…'}
                  value={modalDesc}
                  onChange={(e) => setModalDesc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">
                  {language === 'pt-BR' ? 'Preço (por unidade)' : 'Price (per unit)'}
                </label>
                <Input
                  data-testid="preset-modal-price"
                  type="number"
                  placeholder="0.00"
                  value={modalPrice}
                  onChange={(e) => setModalPrice(e.target.value)}
                  step="0.01"
                  min="0"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                disabled={saving}
                data-testid="preset-modal-cancel"
              >
                {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="preset-modal-save"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Check className="h-4 w-4 mr-1" />
                {saving
                  ? (language === 'pt-BR' ? 'Salvando…' : 'Saving…')
                  : (language === 'pt-BR' ? 'Salvar' : 'Save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExtraLaborPresetsDropdown;
